import React, { useState, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  ShareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  UsersIcon,
  ClockIcon,
  GlobeAltIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Card, CardContent } from "../Shared/Card";
import { Button } from "../Shared/Button";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../hooks/useToast";
import { screenshotService } from "../../services/screenshotService";
import { cn } from "../../utils/cn";

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    description: string;
    url: string;
    screenshot_url?: string;
    created_at: string;
    member_count?: number;
    guest_password: string;
    screenshot_updated_at?: string;
  };
  onShare: () => void;
  onDeleted: () => void;
  onUpdated?: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onShare,
  onDeleted,
  onUpdated,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshingScreenshot, setIsRefreshingScreenshot] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [currentScreenshotUrl, setCurrentScreenshotUrl] = useState(
    project.screenshot_url
  );
  const { showToast } = useToast();

  // Update local screenshot URL when project prop changes
  useEffect(() => {
    setCurrentScreenshotUrl(project.screenshot_url);
  }, [project.screenshot_url]);

  const getWidgetUrl = (url: string) => {
    // Return the actual website URL - the widget should be installed there
    return url;
  };

  const getWidgetCode = () => {
    return `<!-- FeedbackFlow Widget -->
<script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script src="https://feedbackflownew.netlify.app/widget/widget.iife.js"></script>
<link rel="stylesheet" href="https://feedbackflownew.netlify.app/style.css">

<script>
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      if (!window.FeedbackFlowWidget) {
        throw new Error('FeedbackFlowWidget failed to load');
      }
      await window.FeedbackFlowWidget.init({
        projectId: "${project.guest_password}"
      });
      console.log('Widget initialized successfully');
    } catch (error) {
      console.error('Widget initialization failed:', error);
    }
  });
</script>`;
  };

  const handleCopySnippet = async () => {
    try {
      await navigator.clipboard.writeText(getWidgetCode());
      showToast("Widget code copied to clipboard!", "success");
    } catch (error) {
      showToast("Failed to copy widget code", "error");
    }
  };

  const handleRefreshScreenshot = async () => {
    setIsRefreshingScreenshot(true);
    try {
      // Capture new screenshot with force refresh to bypass all caching
      const newScreenshotUrl = await screenshotService.captureWebsiteScreenshot(
        project.url,
        true // Force refresh - bypass all caching
      );

      if (
        newScreenshotUrl &&
        newScreenshotUrl !== "/images/placeholder-screenshot.png"
      ) {
        // Update project with new screenshot and add timestamp for cache busting
        const { error } = await supabase
          .from("projects")
          .update({
            screenshot_url: newScreenshotUrl,
            screenshot_updated_at: new Date().toISOString(),
          })
          .eq("id", project.id);

        if (error) {
          throw error;
        }

        // Update local state immediately to show new screenshot
        setCurrentScreenshotUrl(newScreenshotUrl);

        // Force image reload by resetting states
        setImageLoaded(false);
        setImageError(false);

        showToast("Screenshot updated successfully!", "success");

        // Trigger parent refresh to update the projects list
        onUpdated?.();
      } else {
        throw new Error("Failed to capture new screenshot");
      }
    } catch (error) {
      console.error("Error refreshing screenshot:", error);
      showToast(
        error instanceof Error ? error.message : "Failed to update screenshot",
        "error"
      );
    } finally {
      setIsRefreshingScreenshot(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this project? This action cannot be undone."
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      // Get current session and user
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error getting session:", sessionError);
        throw new Error("Failed to verify authentication session");
      }

      if (!session) {
        throw new Error("No active session found. Please log in again.");
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        console.error("Error getting user:", userError);
        throw new Error("Failed to verify user authentication");
      }

      if (!user) {
        throw new Error("You must be logged in to delete a project");
      }

      // First verify that the user is the owner
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("owner_id")
        .eq("id", project.id)
        .single();

      if (projectError) {
        console.error("Error fetching project:", projectError);
        throw new Error("Failed to verify project ownership");
      }

      if (!projectData) {
        throw new Error("Project not found");
      }

      if (projectData.owner_id !== user.id) {
        throw new Error("You don't have permission to delete this project");
      }

      // Call the delete-project edge function
      const { data: deleteData, error: deleteError } =
        await supabase.functions.invoke("delete-project", {
          body: { projectId: project.id, userId: user.id },
        });

      if (deleteError) {
        console.error("Error deleting project:", {
          error: deleteError,
          message: deleteError.message,
          details: deleteError.details,
          hint: deleteError.hint,
        });
        throw new Error(`Failed to delete project: ${deleteError.message}`);
      }

      if (!deleteData?.success) {
        console.error("Project deletion failed:", deleteData);
        throw new Error("Failed to delete project: Operation unsuccessful");
      }

      showToast("Project deleted successfully", "success");
      onDeleted();
    } catch (error) {
      console.error("Error deleting project:", error);
      showToast(
        error instanceof Error ? error.message : "Failed to delete project",
        "error"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getDomainFromUrl = (url: string) => {
    try {
      return new URL(url).hostname.replace("www.", "");
    } catch {
      return url;
    }
  };

  return (
    <Card hover className="group overflow-hidden h-full flex flex-col">
      {/* Screenshot section */}
      <div className="relative">
        <div className="aspect-video w-full bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          {currentScreenshotUrl && !imageError ? (
            <>
              {/* Loading placeholder */}
              <div
                className={cn(
                  "absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 transition-opacity duration-500",
                  imageLoaded ? "opacity-0" : "opacity-100"
                )}
              >
                <div className="absolute inset-0 bg-shimmer bg-[length:200%_100%] animate-shimmer" />
              </div>

              {/* Actual image */}
              <img
                key={`${project.id}-${currentScreenshotUrl}`}
                src={currentScreenshotUrl}
                alt={project.name}
                className={cn(
                  "w-full h-full object-cover transition-all duration-500 group-hover:scale-105",
                  imageLoaded ? "opacity-100" : "opacity-0"
                )}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <GlobeAltIcon className="h-12 w-12 mb-2" />
              <span className="text-sm font-medium">No preview available</span>
            </div>
          )}

          {/* Refresh Screenshot Button */}
          <div className="absolute top-3 right-3">
            <Button
              size="xs"
              variant="secondary"
              onClick={handleRefreshScreenshot}
              isLoading={isRefreshingScreenshot}
              leftIcon={
                !isRefreshingScreenshot ? (
                  <ArrowPathIcon className="h-3 w-3" />
                ) : undefined
              }
              className="bg-white/90 backdrop-blur-sm hover:bg-white shadow-soft"
              title="Refresh screenshot"
            >
              {isRefreshingScreenshot ? "Updating..." : "Refresh"}
            </Button>
          </div>

          {/* Status indicator */}
          <div className="absolute top-3 left-3">
            <div className="flex items-center gap-2 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 shadow-soft">
              <div className="h-2 w-2 rounded-full bg-success-500" />
              <span>Live</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content - flex-1 to push actions to bottom */}
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-900 mb-1 group-hover:text-gray-700 transition-colors line-clamp-1">
            {project.name}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
            {project.description}
          </p>
        </div>

        {/* URL */}
        <div className="flex items-center gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
          <GlobeAltIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 truncate">
            {getDomainFromUrl(project.url)}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <UsersIcon className="h-4 w-4" />
            <span>
              {project.member_count || 1} member
              {(project.member_count || 1) === 1 ? "" : "s"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ClockIcon className="h-4 w-4" />
            <span>
              {formatDistanceToNow(new Date(project.created_at), {
                addSuffix: true,
              })}
            </span>
          </div>
        </div>

        {/* Actions - pushed to bottom with mt-auto */}
        <div className="mt-auto space-y-2">
          {/* Row 1: View Site and Share */}
          <div className="flex gap-2">
            <Button
              as="a"
              href={getWidgetUrl(project.url)}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="outline"
              leftIcon={<EyeIcon className="h-4 w-4" />}
              className="flex-1 text-center"
            >
              View Site
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={onShare}
              leftIcon={<ShareIcon className="h-4 w-4" />}
              className="flex-1"
            >
              Share
            </Button>
          </div>

          {/* Row 2: Copy Widget and Delete */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCopySnippet}
              leftIcon={<DocumentDuplicateIcon className="h-4 w-4" />}
              className="flex-1"
            >
              Copy Widget
            </Button>

            <Button
              size="sm"
              variant="danger"
              onClick={handleDelete}
              isLoading={isDeleting}
              leftIcon={
                !isDeleting ? <TrashIcon className="h-4 w-4" /> : undefined
              }
              className="flex-1"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
